import React, { useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiCode,
  FiCheckCircle,
  FiMicroscope,
  FiPenTool,
  FiLink,
  FiBook,
  FiPlayCircle
} = FiIcons

const EnrichmentBlocks = () => {
  const [completed, setCompleted] = useState({
    stem: false,
    arts: false,
    coding: false
  })

  const toggleComplete = (key) => {
    setCompleted((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-3">Enrichment Blocks</h1>
        <p className="text-blue-50 max-w-3xl">
          Weekly STEM, arts, and coding experiences with ready-to-use instructions, resources, and
          completion tracking for students and mentors.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiMicroscope} className="w-6 h-6 text-[#00AAFF]" />
              <h3 className="text-lg font-bold text-gray-800">Weekly STEM Project</h3>
            </div>
            <button
              onClick={() => toggleComplete('stem')}
              className={`text-sm px-3 py-1 rounded-full ${
                completed.stem ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {completed.stem ? 'Completed' : 'Mark complete'}
            </button>
          </div>
          <p className="text-sm text-gray-700">
            Build a tabletop water filter. Test different materials (sand, gravel, charcoal), record
            flow rates, and reflect on clean water access.
          </p>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• Supplies: plastic bottle, coffee filter, sand/gravel, timer</li>
            <li>• Challenge: Improve clarity and taste scores with iteration</li>
            <li>• Extension: Graph results and compare to municipal standards</li>
          </ul>
          <a className="flex items-center text-[#1B4C87] text-sm" href="https://example.com/stem" target="_blank" rel="noreferrer">
            <SafeIcon icon={FiLink} className="w-4 h-4 mr-1" />
            Download instructions
          </a>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiPenTool} className="w-6 h-6 text-[#F97316]" />
              <h3 className="text-lg font-bold text-gray-800">Arts & Digital Creation</h3>
            </div>
            <button
              onClick={() => toggleComplete('arts')}
              className={`text-sm px-3 py-1 rounded-full ${
                completed.arts ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {completed.arts ? 'Completed' : 'Mark complete'}
            </button>
          </div>
          <p className="text-sm text-gray-700">
            Create a mini zine on &ldquo;Moments of Joy.&rdquo; Sketch, photograph, or design digital
            spreads. Export to PDF for the Friday Showcase.
          </p>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• Include at least one original photo and one illustration</li>
            <li>• Add a caption describing the feeling or memory</li>
            <li>• Share a 30-second artist statement video</li>
          </ul>
          <a className="flex items-center text-[#1B4C87] text-sm" href="https://example.com/arts" target="_blank" rel="noreferrer">
            <SafeIcon icon={FiBook} className="w-4 h-4 mr-1" />
            Inspiration board
          </a>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCode} className="w-6 h-6 text-[#10B981]" />
              <h3 className="text-lg font-bold text-gray-800">Coding Template</h3>
            </div>
            <button
              onClick={() => toggleComplete('coding')}
              className={`text-sm px-3 py-1 rounded-full ${
                completed.coding ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {completed.coding ? 'Completed' : 'Mark complete'}
            </button>
          </div>
          <p className="text-sm text-gray-700">
            Remix the starter code for a reaction-timer game. Track high scores and add a calming
            background soundtrack.
          </p>
          <div className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs font-mono">
            <p>// Optional embedded playground placeholder</p>
            <p>const beats = [300, 500, 700]</p>
            <p>function startRound() &#123; /* launch lights + audio */ &#125;</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#1B4C87]">
            <SafeIcon icon={FiPlayCircle} className="w-4 h-4" />
            Open in code playground
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-3">Completion Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: 'stem', label: 'STEM Project' },
            { key: 'arts', label: 'Arts & Digital Creation' },
            { key: 'coding', label: 'Coding Template' }
          ].map((item) => (
            <div
              key={item.key}
              className={`p-4 rounded-lg border-2 ${
                completed[item.key] ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-800">{item.label}</p>
                <SafeIcon
                  icon={FiCheckCircle}
                  className={`w-5 h-5 ${completed[item.key] ? 'text-green-600' : 'text-gray-400'}`}
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {completed[item.key] ? 'Completed for this week' : 'Tap to mark as complete'}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default EnrichmentBlocks
